import {ClientSideRowModelModule} from '@ag-grid-community/client-side-row-model';
import {GridOptions, ICellRendererParams, Module} from '@ag-grid-community/core';
import {AfterViewInit, Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {ModalController} from '@ionic/angular';
import {DialogAddPatientComponent} from '@modules/doctor-panel/components/dialog-add-patient/dialog-add-patient.component';
import {DoctorPanelActions} from '@modules/doctor-panel/store/doctor-panel.actions';
import {DoctorPanelState} from '@modules/doctor-panel/store/doctor-panel.state';
import {Select, Store} from '@ngxs/store';
import {AG_GRID_LOCALE_IT, C3TmplCellComponent, C3TmplCellOptions} from '@trec/components/table';
import {format} from 'date-fns';
import {it as localeIt} from "date-fns/locale";
import {Observable} from 'rxjs';
import {DialogEnrolmentPatientMmgNotes} from '../dialog-enrolment-patient-mmg-notes/dialog-enrolment-patient-mmg-notes';
import {DialogEnrolmentPatient} from '../dialog-enrolment-patient/dialog-enrolment-patient';
import {EnrolmentActionsCell} from './cell-rendered/enrolment-actions-cell/enrolment-actions-cell';
import {EnrolmentStatus} from './enrolment-status';

@Component({
  selector: 'app-enrolment-requests-list',
  templateUrl: './enrolment-requests-list.html',
  styleUrl: './enrolment-requests-list.scss',
  standalone: false,
})

export class EnrolmentRequestsList implements OnInit, AfterViewInit {
  // Child components /////////////////////////////////////////////////////////////////////////////
  @ViewChild("descriptionCellTmpl")
  descriptionCellTmpl: TemplateRef<unknown>;

  @ViewChild("mmgNotesCellTmpl")
  mmgNotesCellTmpl!: TemplateRef<unknown>;

  @ViewChild("actionsCellTmpl")
  actionsCellTmpl!: TemplateRef<unknown>;

  columnDefs: any[] = [];
  rowData: any[] = [];
  rowHeight = 80;
  // Angular
  agModules: Module[] = [ClientSideRowModelModule];
  structureId = localStorage.getItem("c3-curr-structure");

  // Store selectors /////////////////////////////////////////////////////////////////////////////
  @Select(DoctorPanelState.listaRequestPatientsToEnrol) rowData$: Observable<any[]>;

  // AG Grid
  agGridOptions: GridOptions = {
    localeText: AG_GRID_LOCALE_IT,
    context: {componentParent: this},
    defaultColDef: {sortable: true, filter: true, resizable: true},
    pagination: true,
    paginationPageSize: 20,
    // keep page size controlled via paginationPageSize; remove unsupported option
    // paginationAutoPageSize: true,
    suppressCellFocus: true,
    suppressRowHoverHighlight: true,
    domLayout: "autoHeight",
    // registra il renderer con un nome
    components: {
      enrolmentActionsCell: EnrolmentActionsCell,
    },
  };

  constructor(
    //private saluteHelperService: SaluteHelperService,
    private modalCtrl: ModalController,
    private store: Store
  ) {
  }

  ngOnInit() {
    console.log('EnrolmentRequestsList initialized, loading enrolment requests list...');
    this.store.dispatch(new DoctorPanelActions.ListaRequestPatientsToEnrol({structureId: this.structureId!, status: EnrolmentStatus.PENDING}));
  }

  ngAfterViewInit() {
    this.columnDefs = [
      {
        headerName: 'Paziente',
        valueGetter: (params) => {
          return `${params.data.physicianName || ''} ${params.data.physicianSurname || ''}`.trim();
        },
        flex: 10,
        cellRenderer: '',
        cellClass: ''
      },
      {
        headerName: 'Codice Fiscale',
        field: 'patientFiscalCode',
        flex: 10,
        cellRenderer: '',
      },
      {
        headerName: 'Contesto',
        field: 'context',
        flex: 10,
        cellRenderer: '',
      },
      {
        headerName: 'Note',
        field: 'mmgNotes',
        flex: 10,
        cellRendererSelector: () => ({component: C3TmplCellComponent}),
        cellRendererParams: {template: this.mmgNotesCellTmpl} as C3TmplCellOptions,
      },
      {
        headerName: 'Data Richiesta',
        field: 'createTime',
        flex: 10,
        cellRenderer: (params: ICellRendererParams) => {
          try {
            const v = params.value;
            if (v === null || v === undefined) return '-';
            const d = v instanceof Date ? v : new Date(v);
            if (isNaN(d.getTime())) return '-';
            return format(d, 'dd/MM/yyyy', {locale: localeIt});
          } catch {
            return '-';
          }
        },
      },
      {
        headerName: 'Azioni',
        field: '',
        flex: 10,
        cellRendererSelector: () => ({component: C3TmplCellComponent}),
        cellRendererParams: {template: this.actionsCellTmpl} as C3TmplCellOptions,
      },
    ];

    if ((this as any).gridApi && (this as any).gridApi.setColumnDefs) {
      (this as any).gridApi.setColumnDefs(this.columnDefs);
    }
  }

  async onOpenAddPatient(data: any) {
    const modal = await this.modalCtrl.create({
      component: DialogAddPatientComponent,
      cssClass: "c3-modal-width-sm c3-modal-height-lg",
      componentProps: {
        isArruolamento: true,
        patientFiscalCode: data.patientFiscalCode,
        disabledFiscalCode: true,
        selectedContextInput: data.context,
        patientId: data.id,
        isRichiestaArruolamento: true,
        selectedContext: true
      },
    });
    modal.onDidDismiss().then((data) => {
      if (data && data.data && data.data.pdtaSaved) {
        //Approva richiesta di arruolamento esistente per il paziente. 
        this.store
          .dispatch(
            new DoctorPanelActions.ApproveDeniedRequestPatientsToEnrol({
              id: data.data?.patientId,
              approval: {status: EnrolmentStatus.APPROVED},
            })).subscribe((response: any) => {
              if (response) {
                // chiudi dialogo e aggiorna lista richieste in PENDING stato
                this.store.dispatch(new DoctorPanelActions.ListaRequestPatientsToEnrol({structureId: this.structureId!, status: EnrolmentStatus.PENDING}));
              }
            });
      }
    });
    return await modal.present();
  }


  async enrolmentActions(event: Event, data: any, isApprove: boolean) {
    //console.log('Opening add patient modal for enrolment with data:', data);
    event.stopPropagation();
    const modal = await this.modalCtrl.create({
      component: DialogEnrolmentPatient,
      cssClass: "c3-modal-width-sm c3-modal-height-lg",
      componentProps: {
        patientData: data,
        isApprove: isApprove
      },
    });
    modal.onDidDismiss().then((data) => {
      if (data && data.data && data.data.pdtaSaved) {
        // Aggiorna la lista delle richieste di arruolamento in stato PENDING
        this.store.dispatch(new DoctorPanelActions.ListaRequestPatientsToEnrol({structureId: this.structureId!, status: EnrolmentStatus.PENDING}));
      }
    });
    return await modal.present();
  }

  async viewsNotes(event: Event, data: any) {
    // Implementa la logica per visualizzare le note del MMG
    const modal = await this.modalCtrl.create({
      component: DialogEnrolmentPatientMmgNotes,
      cssClass: "c3-modal-width-sm c3-modal-height-sm",
      componentProps: {
        mmgNotes: data.mmgNotes,
      },
    });
    return await modal.present();
  }

  // AG Grid api handles
  onGridReady(params: any) {
    (this as any).gridApi = params.api;
    (this as any).gridColumnApi = params.columnApi;
    // If columnDefs are already built, apply them if the API exposes the method
    if (this.columnDefs && this.columnDefs.length) {
      const api = (this as any).gridApi || params.api;
      const colApi = (this as any).gridColumnApi || params.columnApi;
      if (api && typeof api.setColumnDefs === 'function') {
        api.setColumnDefs(this.columnDefs);
      } else if (colApi && typeof colApi.setColumnDefs === 'function') {
        // fallback in case the api exposes it on columnApi (defensive)
        colApi.setColumnDefs(this.columnDefs);
      } else {
        // last resort: set columnDefs via gridOptions and refresh
        if (this.agGridOptions && (this.agGridOptions as any).api && typeof (this.agGridOptions as any).api.setColumnDefs === 'function') {
          (this.agGridOptions as any).api.setColumnDefs(this.columnDefs);
        }
      }
    }
  }

}
